import React, {useEffect, useState} from "react";
import {Card, Button} from "react-bootstrap";
import {collection, getDocs, query, where, Timestamp} from "@firebase/firestore";
import {db} from "../../../firebase/FirebaseProvider";
import {User} from "../../types/Types";
import TimeSeriesGraph from "./TimeSeriesGraph";

interface HourlyData {
    hour: string;
    count: number;
    timestamp: Date;
}

function LoginGraphCard() {
    const [hourlyLoginData, setHourlyLoginData] = useState<HourlyData[]>([]);
    const [graphLoading, setGraphLoading] = useState<boolean>(true);
    const [loginTimeScale, setLoginTimeScale] = useState<'day' | 'week'>('day');

    useEffect(() => {
        (async () => {
            await loadLoginGraph();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loginTimeScale]);

    const loadLoginGraph = async () => {
        try {
            setGraphLoading(true);
            
            const now = new Date();
            let cutoffDate: Date;
            let buckets: Map<string, number>;
            
            if (loginTimeScale === 'day') {
                // 24 hours ago
                cutoffDate = new Date(now);
                cutoffDate.setHours(now.getHours() - 24);
                
                // Initialize hourly buckets
                buckets = new Map<string, number>();
                for (let i = 23; i >= 0; i--) {
                    const hourDate = new Date(now);
                    hourDate.setHours(now.getHours() - i, 0, 0, 0);
                    buckets.set(hourDate.toISOString(), 0);
                }
            } else {
                // 7 days ago
                cutoffDate = new Date(now);
                cutoffDate.setDate(now.getDate() - 7);
                
                // Initialize daily buckets
                buckets = new Map<string, number>();
                for (let i = 6; i >= 0; i--) {
                    const dayDate = new Date(now);
                    dayDate.setDate(now.getDate() - i);
                    dayDate.setHours(0, 0, 0, 0);
                    buckets.set(dayDate.toISOString(), 0);
                }
            }
            
            const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

            // Query users where lastSeenAt is within the timeframe
            const usersQuery = query(
                collection(db, 'users'),
                where('lastSeenAt', '>=', cutoffTimestamp)
            );

            const querySnapshot = await getDocs(usersQuery);
            const loginTimes: Date[] = [];
            
            querySnapshot.forEach((doc) => {
                const userData = doc.data() as User;
                if (userData.lastSeenAt) {
                    const lastSeenAny = userData.lastSeenAt as any;
                    const lastSeen = lastSeenAny.toDate ? lastSeenAny.toDate() : new Date(userData.lastSeenAt);
                    loginTimes.push(lastSeen);
                }
            });

            // Count logins per bucket
            loginTimes.forEach(loginTime => {
                const bucketStart = new Date(loginTime);
                if (loginTimeScale === 'day') {
                    bucketStart.setMinutes(0, 0, 0);
                } else {
                    bucketStart.setHours(0, 0, 0, 0);
                }
                
                // Find the closest bucket
                let closestKey = bucketStart.toISOString();
                let minDiff = Infinity;
                buckets.forEach((_, mapKey) => {
                    const diff = Math.abs(new Date(mapKey).getTime() - bucketStart.getTime());
                    if (diff < minDiff) {
                        minDiff = diff;
                        closestKey = mapKey;
                    }
                });
                
                buckets.set(closestKey, (buckets.get(closestKey) || 0) + 1);
            });

            // Convert to array and format
            const hourlyData: HourlyData[] = Array.from(buckets.entries())
                .map(([timestamp, count]) => {
                    const date = new Date(timestamp);
                    let label: string;
                    if (loginTimeScale === 'day') {
                        label = `${date.getHours()}`;
                    } else {
                        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                        label = dayNames[date.getDay()];
                    }
                    return {
                        hour: label,
                        count,
                        timestamp: date
                    };
                })
                .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

            console.log(`Loaded login graph data: ${hourlyData.length} documents`);
            setHourlyLoginData(hourlyData);
        } catch (error) {
            console.error("Error loading login graph:", error);
        } finally {
            setGraphLoading(false);
        }
    };

    const maxCount = Math.max(...hourlyLoginData.map(d => d.count), 1);

    return (
        <Card className={"mt-3 mb-3"}>
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <div><b>Login Graph</b></div>
                        {!graphLoading && (
                            <small className="text-muted">Peak: {maxCount} per {loginTimeScale === 'day' ? 'hour' : 'day'}</small>
                        )}
                    </div>
                    <div>
                        <Button 
                            className={"btn-sm mr-2 cursor-pointer"} 
                            disabled={loginTimeScale === 'day'}
                            onClick={() => setLoginTimeScale('day')}
                        >
                            Day
                        </Button>
                        <Button 
                            className={"btn-sm cursor-pointer"} 
                            disabled={loginTimeScale === 'week'}
                            onClick={() => setLoginTimeScale('week')}
                        >
                            Week
                        </Button>
                    </div>
                </div>
            </Card.Header>
            <Card.Body style={{ padding: "1em" }}>
                <TimeSeriesGraph
                    data={hourlyLoginData}
                    maxCount={maxCount}
                    loading={graphLoading}
                    color="#10b981"
                    timeScale={loginTimeScale}
                    dataLabel="login"
                />
            </Card.Body>
        </Card>
    );
}

export default LoginGraphCard;
